import React, { useState } from "react";
import { IonButton, IonCard, IonCardContent, IonCardHeader, IonCardTitle, IonIcon, IonInput, IonItem, IonLabel, IonList, IonSpinner, IonText } from "@ionic/react";
import { copyOutline, folderOpenOutline, saveOutline } from "ionicons/icons";
import { StudentEnrollmentFromServer } from "../../../data-types/Types";
import { useData } from "../services/DataProvider";
import { useRoster } from "../services/RosterProvider";
import DeleteConfirmButton from "./DeleteConfirmButton";
import "./RosterManager.css";

type RosterManagerProps = {
  classCode: string;
  enrollments: StudentEnrollmentFromServer[];
};

const downloadJson = (filename: string, content: string) => {
  const blob = new Blob([content], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
};

const RosterManager: React.FC<RosterManagerProps> = ({ classCode, enrollments }) => {
  const { createEnrollments, deleteEnrollment } = useData();
  const { getName, setName, setNamesBatch, removeName, exportRoster, hasSavedSinceMutation, getRoster } = useRoster();
  const [newName, setNewName] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [pendingNames, setPendingNames] = useState<Record<string, string>>({});
  const [isCreating, setIsCreating] = useState(false);
  const selectText = (target: HTMLElement) => {
    const selection = window.getSelection();
    if (!selection) {
      return;
    }
    const range = document.createRange();
    range.selectNodeContents(target);
    selection.removeAllRanges();
    selection.addRange(range);
  };
  const copyStudentId = async (studentId: string) => {
    if (!studentId) {
      return;
    }
    try {
      await navigator.clipboard.writeText(studentId);
    } catch {
      // Ignore clipboard failures (e.g., blocked permissions).
    }
  };

  const handleDelete = async (enrollment: StudentEnrollmentFromServer) => {
    if (!enrollment.id || !enrollment.studentPassword) {
      return;
    }
    await deleteEnrollment(enrollment.id);
    removeName(classCode, enrollment.studentPassword);
  };

  const normalizeName = (value: string) => value.trim().replace(/\s+/g, " ").toLowerCase();

  const handleBulkCreate = async (names: string[]) => {
    const trimmedNames = names.map((name) => name.trim()).filter(Boolean);
    if (trimmedNames.length === 0) {
      return;
    }
    const existingNames = Object.values(getRoster(classCode)).map((name) => normalizeName(name));
    const existingSet = new Set(existingNames);
    const uniqueNames: string[] = [];
    trimmedNames.forEach((name) => {
      const normalized = normalizeName(name);
      if (!normalized || existingSet.has(normalized)) {
        return;
      }
      existingSet.add(normalized);
      uniqueNames.push(name);
    });

    if (uniqueNames.length === 0) {
      return;
    }

    setError(null);
    const created = await createEnrollments(classCode, uniqueNames.length);
    const batchEntries: Record<string, string> = {};
    uniqueNames.forEach((name, index) => {
      const createdEnrollment = created[index];
      const rosterKey = createdEnrollment?.studentPassword || createdEnrollment?.id;
      if (rosterKey) {
        batchEntries[rosterKey] = name;
      }
    });
    setNamesBatch(classCode, batchEntries);
  };

  const handleFileImport = async (file: File) => {
    setError(null);
    const text = await file.text();
    let payload: { classCode: string; students: Array<{ id: string; name: string }> };
    try {
      payload = JSON.parse(text);
    } catch {
      setError("Invalid JSON file.");
      return;
    }
    if (!payload?.classCode || payload.classCode !== classCode || !Array.isArray(payload.students)) {
      setError("Roster file does not match this class.");
      return;
    }
    const existingIds = new Set(enrollments.map((enrollment) => enrollment.studentPassword || enrollment.id || ""));
    const existingEntries: Record<string, string> = {};
    let hasChanges = false;
    payload.students.forEach((student) => {
      if (existingIds.has(student.id)) {
        const incomingName = student.name || "";
        existingEntries[student.id] = incomingName;
        const currentName = getName(classCode, student.id) || "";
        if (currentName !== incomingName) {
          hasChanges = true;
        }
      }
    });
    if (hasChanges) {
      setNamesBatch(classCode, existingEntries);
    }
  };

  const handleExport = () => {
    const json = exportRoster(classCode);
    downloadJson(`roster-${classCode}.json`, json);
  };

  const hasUnsavedChanges = !hasSavedSinceMutation(classCode);
  const rosterDisabled = isCreating;

  const handleNewRowCommit = async () => {
    if (isCreating) {
      return;
    }
    const trimmedName = newName.trim();
    if (!trimmedName) {
      return;
    }
    setIsCreating(true);
    try {
      await handleBulkCreate([trimmedName]);
      setNewName("");
    } catch (error) {
      const message = error instanceof Error ? error.message : "Failed to create enrollment.";
      setError(message);
    } finally {
      setIsCreating(false);
    }
  };

  const handleTablePaste = async (event: React.ClipboardEvent) => {
    if (isCreating) {
      return;
    }
    const text = event.clipboardData.getData("text") || "";
    const names = text.split(/\r?\n/).map((line) => line.trim()).filter(Boolean);
    if (names.length <= 1) {
      return;
    }
    event.preventDefault();
    await handleBulkCreate(names);
  };

  return (
    <IonCard>
      <IonCardHeader>
        <IonCardTitle>Roster Management</IonCardTitle>
      </IonCardHeader>
      <IonCardContent>
        <p>Roster names are stored only on this device. Export to back them up.</p>
        {hasUnsavedChanges ? (
          <IonText color="danger">
            <p>Roster has unsaved changes.</p>
          </IonText>
        ) : (
          <p>Roster saved.</p>
        )}
        {error && <p>{error}</p>}

        <IonButton
          onClick={handleExport}
          className={hasUnsavedChanges ? "roster-save-pulse" : undefined}
          aria-label="Export roster"
          disabled={rosterDisabled}
        >
          <IonIcon icon={saveOutline} slot="icon-only" />
        </IonButton>
        {!hasUnsavedChanges && enrollments.length > 0 && (
          <IonButton
            onClick={() => document.getElementById("roster-import-input")?.click()}
            aria-label="Import roster"
            disabled={rosterDisabled}
          >
            <IonIcon icon={folderOpenOutline} slot="icon-only" />
          </IonButton>
        )}
        <input
          id="roster-import-input"
          type="file"
          accept="application/json"
          style={{ display: "none" }}
          onChange={(event) => {
            const file = event.target.files?.[0];
            if (file) {
              handleFileImport(file);
            }
            event.target.value = "";
          }}
          disabled={rosterDisabled}
        />

        <IonList onPaste={handleTablePaste}>
          {[...enrollments]
            .map((enrollment) => {
              const studentId = enrollment.studentPassword || enrollment.id || "";
              return {
                enrollment,
                studentId,
                displayName: (getName(classCode, studentId) || "").trim(),
              };
            })
            .sort((a, b) => a.displayName.localeCompare(b.displayName, undefined, { sensitivity: "base" }))
            .map(({ enrollment, studentId, displayName }) => {
              const isEditable = studentId.length > 0;
              const pendingValue = pendingNames[studentId];
              const inputValue = pendingValue !== undefined ? pendingValue : displayName;
              return (
                <IonItem key={enrollment.id || studentId}>
                <IonInput
                  value={inputValue}
                  placeholder="Student name"
                  onIonChange={(event) => {
                    if (!isEditable) {
                      return;
                    }
                    setPendingNames((prev) => ({
                      ...prev,
                      [studentId]: event.detail.value || "",
                    }));
                  }}
                  onIonBlur={() => {
                    if (!isEditable) {
                      return;
                    }
                    const valueToSave = (pendingNames[studentId] ?? inputValue).trim();
                    if (valueToSave !== displayName) {
                      setName(classCode, studentId, valueToSave);
                    }
                    setPendingNames((prev) => {
                      const next = { ...prev };
                      delete next[studentId];
                      return next;
                    });
                  }}
                  onKeyDown={(event) => {
                    if (!isEditable || event.key !== "Enter") {
                      return;
                    }
                    event.preventDefault();
                    const valueToSave = (pendingNames[studentId] ?? inputValue).trim();
                    if (valueToSave !== displayName) {
                      setName(classCode, studentId, valueToSave);
                    }
                    setPendingNames((prev) => {
                      const next = { ...prev };
                      delete next[studentId];
                      return next;
                    });
                  }}
                  disabled={!isEditable || rosterDisabled}
                />
                <IonLabel className="roster-student-id-label">
                  <div>Student ID</div>
                  <div className="roster-student-id">
                    <span onClick={(event) => selectText(event.currentTarget)}>{studentId}</span>
                  </div>
                </IonLabel>
                <IonButton
                  fill="clear"
                  size="small"
                  onClick={() => copyStudentId(studentId)}
                  aria-label={`Copy student ID ${studentId}`}
                  disabled={rosterDisabled}
                >
                  <IonIcon icon={copyOutline} />
                </IonButton>
                <DeleteConfirmButton
                  onConfirm={() => handleDelete(enrollment)}
                  disabled={!enrollment.id || rosterDisabled}
                />
              </IonItem>
              );
            })}
          <IonItem>
            <IonInput
              value={newName}
              placeholder="Add student name"
              onIonChange={(event) => setNewName(event.detail.value || "")}
              onIonBlur={handleNewRowCommit}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault();
                  handleNewRowCommit();
                }
              }}
              disabled={rosterDisabled}
            />
            <IonLabel className="roster-student-id-label">
              <div>Student ID</div>
              <div className="roster-student-id">
                <span>Assigned on save</span>
              </div>
            </IonLabel>
            {isCreating && <IonSpinner slot="end" name="dots" />}
          </IonItem>
        </IonList>
      </IonCardContent>
    </IonCard>
  );
};

export default RosterManager;
